import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import { useConsents } from "../hooks/useConsents";
import DisabledFeature from "./DisabledFeature";
import toast from "react-hot-toast";
import "../styles/JobRecommendations.css";

interface Recommendation {
  id: string;
  jobId: string;
  jobTitle: string;
  company: string;
  location: string;
  salary?: string;
  url?: string;
  matchScore: number;
  matchedSkills: string[];
  matchedEducation: string[];
  matchBasisSkillsMatched: number;
  matchBasisSkillsTotal: number;
  matchBasisEducationMatched: number;
  matchBasisEducationTotal: number;
  matchBasisExperience: boolean;
  matchCategory: "skills" | "education" | "experience" | "combined";
  reason?: string;
}

const JobRecommendations: React.FC = () => {
  const navigate = useNavigate();
  const { consents, loading: consentsLoading } = useConsents();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sortBy, setSortBy] = useState<"score" | "title" | "company">("score");
  const [filterTab, setFilterTab] = useState<"all" | "skills" | "education" | "experience">("all");
  // Phase 5.1 — user-tunable minimum match threshold
  const [minScore, setMinScore] = useState<number>(0);
  // Phase 5.3 — extra filter toggles (multi-select)
  const [extraFilters, setExtraFilters] = useState<{ sameResume: boolean; similarJD: boolean; viewedOnly: boolean }>({
    sameResume: false,
    similarJD: false,
    viewedOnly: false,
  });

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/auth/recommendations");
      setRecommendations(response.data.recommendations);
    } catch (error: any) {
      if (error.response?.status !== 404) {
        toast.error("Failed to load recommendations");
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendations = async () => {
    try {
      setGenerating(true);
      const response = await axiosInstance.post("/auth/recommendations/generate");
      toast.success(response.data.message || `Refreshed ${response.data.count} recommendations`);
      fetchRecommendations();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to generate recommendations");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const ignoreRecommendation = async (jobId: string) => {
    try {
      await axiosInstance.post(`/auth/recommendations/${jobId}/ignore`);
      toast.success("Recommendation ignored");
      setRecommendations(recommendations.filter((r) => r.jobId !== jobId));
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to ignore recommendation");
    }
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return "Excellent Match";
    if (score >= 60) return "Good Match";
    if (score >= 40) return "Fair Match";
    return "Consider Reviewing";
  };

  const getScoreClass = (score: number) => {
    if (score >= 80) return "excellent";
    if (score >= 60) return "good";
    if (score >= 40) return "moderate";
    return "low";
  };

  // Filter recommendations by tab + Phase 5.1 score threshold + Phase 5.3 toggles
  const filteredRecommendations = useMemo(() => {
    return recommendations.filter((rec) => {
      // Tab filter
      if (filterTab === "skills" && !(rec.matchBasisSkillsMatched > 0)) return false;
      if (filterTab === "education" && !(rec.matchBasisEducationMatched > 0)) return false;
      if (filterTab === "experience" && !rec.matchBasisExperience) return false;

      // 5.1 — threshold
      if (rec.matchScore < minScore) return false;

      // 5.3 — extra toggles
      // viewedOnly: requires a `viewedAt` field on rec; tolerated as optional
      if (extraFilters.viewedOnly && !(rec as any).viewedAt) return false;
      // sameResume / similarJD are placeholder filters — currently no per-rec
      // resume metadata is plumbed through. When backend adds it, swap these
      // comparators in.
      if (extraFilters.sameResume && !(rec as any).resumeUsedId) return false;
      if (extraFilters.similarJD && !(rec as any).similarTo) return false;

      return true;
    });
  }, [recommendations, filterTab, minScore, extraFilters]);

  // Sort recommendations
  const sortedRecommendations = useMemo(() => {
    return [...filteredRecommendations].sort((a, b) => {
      if (sortBy === "score") return b.matchScore - a.matchScore;
      if (sortBy === "title") return a.jobTitle.localeCompare(b.jobTitle);
      if (sortBy === "company") return a.company.localeCompare(b.company);
      return 0;
    });
  }, [filteredRecommendations, sortBy]);

  // Count recommendations by category
  const categoryCounts = useMemo(() => ({
    all: recommendations.length,
    skills: recommendations.filter(r => r.matchBasisSkillsMatched > 0).length,
    education: recommendations.filter(r => r.matchBasisEducationMatched > 0).length,
    experience: recommendations.filter(r => r.matchBasisExperience).length,
  }), [recommendations]);

  // Check if data_storage and ai_review consents are granted
  if (!consentsLoading && !consents.data_storage) {
    return (
      <div className="hb-recommendations-container">
        <div className="hb-coach-message" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <span className="hb-coach-icon">⭐</span>
          <p>Get personalized job recommendations tailored to your skills!</p>
        </div>
        <h2 className="hb-recommendations-title">Job Recommendations</h2>
        <DisabledFeature
          icon="⭐"
          featureName="Job Recommendations"
          consentType="data_storage"
          description="To receive personalized job recommendations based on your skills and resume, please grant data storage permission in Settings."
        />
      </div>
    );
  }

  if (!consentsLoading && !consents.ai_review) {
    return (
      <div className="hb-recommendations-container">
        <div className="hb-coach-message" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <span className="hb-coach-icon">⭐</span>
          <p>AI-powered matching helps find your perfect opportunities!</p>
        </div>
        <h2 className="hb-recommendations-title">Job Recommendations</h2>
        <DisabledFeature
          icon="⭐"
          featureName="Job Recommendations"
          consentType="ai_review"
          description="To use AI-powered job matching and recommendations, please grant AI review permission in Settings."
        />
      </div>
    );
  }

  return (
    <div className="hb-recommendations-container">
      <button onClick={() => navigate("/dashboard")} className="hb-btn hb-btn-outline" style={{ marginBottom: 'var(--spacing-lg)' }}>
        ← Back to Dashboard
      </button>

      {/* Coach Message */}
      <div className="hb-coach-message" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <span className="hb-coach-icon">⭐</span>
        <p>Every recommendation is a step closer to your next opportunity!</p>
      </div>

      <div className="hb-recommendations-header">
        <h1 className="hb-recommendations-title">Your Job Recommendations</h1>
      </div>

      {/* Generate & Sort Section */}
      <div className="hb-recommendations-actions">
        <button
          onClick={generateRecommendations}
          disabled={generating}
          className="hb-btn hb-btn-primary"
        >
          {generating ? "Finding matches..." : "🔍 Generate New Recommendations"}
        </button>

        <div className="hb-recommendations-sort">
          <label className="hb-recommendations-sort-label">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as "score" | "title" | "company")}
            className="hb-input"
          >
            <option value="score">Best Match First</option>
            <option value="title">Job Title</option>
            <option value="company">Company</option>
          </select>
        </div>
      </div>

      {/* Filter Tabs */}
      {recommendations.length > 0 && (
        <div className="hb-recommendations-tabs">
          <button
            className={`hb-recommendations-tab ${filterTab === "all" ? "active" : ""}`}
            onClick={() => setFilterTab("all")}
          >
            📊 All ({categoryCounts.all})
          </button>
          <button
            className={`hb-recommendations-tab ${filterTab === "skills" ? "active" : ""}`}
            onClick={() => setFilterTab("skills")}
          >
            💻 Skills ({categoryCounts.skills})
          </button>
          <button
            className={`hb-recommendations-tab ${filterTab === "education" ? "active" : ""}`}
            onClick={() => setFilterTab("education")}
          >
            🎓 Education ({categoryCounts.education})
          </button>
          <button
            className={`hb-recommendations-tab ${filterTab === "experience" ? "active" : ""}`}
            onClick={() => setFilterTab("experience")}
          >
            💼 Experience ({categoryCounts.experience})
          </button>
        </div>
      )}

      {/* Phase 5.1 + 5.3 — threshold slider + extra filter toggles */}
      {recommendations.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            padding: "12px 16px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            margin: "12px 0",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155" }}>
            <span style={{ fontWeight: 600 }}>Min match:</span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={minScore}
              onChange={(e) => setMinScore(parseInt(e.target.value, 10))}
              aria-label="Minimum match score"
              style={{ accentColor: "#1d4ed8" }}
            />
            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#1d4ed8", minWidth: 40 }}>
              {minScore}%
            </span>
          </label>

          <span style={{ borderLeft: "1px solid #cbd5e1", height: 20 }} />

          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={extraFilters.viewedOnly}
              onChange={(e) => setExtraFilters((f) => ({ ...f, viewedOnly: e.target.checked }))}
            />
            Viewed only
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={extraFilters.sameResume}
              onChange={(e) => setExtraFilters((f) => ({ ...f, sameResume: e.target.checked }))}
            />
            Same résumé used
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={extraFilters.similarJD}
              onChange={(e) => setExtraFilters((f) => ({ ...f, similarJD: e.target.checked }))}
            />
            Similar JD
          </label>
        </div>
      )}

      {/* Recommendations List */}
      {loading ? (
        <div className="hb-recommendations-loading">
          <p>Finding your perfect matches...</p>
        </div>
      ) : recommendations.length === 0 ? (
        <div className="hb-recommendations-empty">
          <div className="hb-recommendations-empty-icon">🎯</div>
          <h3 className="hb-recommendations-empty-title">No recommendations yet</h3>
          <p className="hb-recommendations-empty-text">
            Click "Generate New Recommendations" to discover jobs tailored to your skills and experience!
          </p>
          <button onClick={generateRecommendations} disabled={generating} className="hb-btn hb-btn-primary">
            {generating ? "Finding matches..." : "Get Started"}
          </button>
        </div>
      ) : sortedRecommendations.length === 0 ? (
        <div className="hb-recommendations-empty">
          <div className="hb-recommendations-empty-icon">📭</div>
          <h3 className="hb-recommendations-empty-title">No matches in this category</h3>
          <p className="hb-recommendations-empty-text">
            Try selecting a different filter to see more recommendations.
          </p>
        </div>
      ) : (
        <div className="hb-recommendations-grid">
          {sortedRecommendations.map((rec) => (
            <div key={rec.id} className="hb-recommendation-card">
              <div className="hb-recommendation-header">
                <h3 className="hb-recommendation-title">{rec.jobTitle}</h3>
                <p className="hb-recommendation-company">{rec.company}</p>
                <p className="hb-recommendation-location">📍 {rec.location}</p>
              </div>

              {rec.salary && (
                <p className="hb-recommendation-salary">💰 {rec.salary}</p>
              )}

              {/* Match Score */}
              <div className="hb-recommendation-score">
                <div className="hb-recommendation-score-header">
                  <span className="hb-recommendation-score-label">{getScoreLabel(rec.matchScore)}</span>
                  <span className="hb-recommendation-score-value">{Math.round(rec.matchScore)}%</span>
                </div>
                <div className="hb-recommendation-score-bar">
                  <div
                    className={`hb-recommendation-score-fill ${getScoreClass(rec.matchScore)}`}
                    style={{ width: `${rec.matchScore}%` }}
                  />
                </div>
              </div>

              {/* Match Basis Breakdown */}
              {(rec.matchBasisSkillsMatched || rec.matchBasisEducationMatched || rec.matchBasisExperience) && (() => {
                // Phase 5.2 — Strength Highlight: in combined matches, bold the strongest dimension.
                const skillsPct = rec.matchBasisSkillsTotal > 0
                  ? rec.matchBasisSkillsMatched / rec.matchBasisSkillsTotal
                  : 0;
                const educationPct = rec.matchBasisEducationTotal > 0
                  ? rec.matchBasisEducationMatched / rec.matchBasisEducationTotal
                  : 0;
                const experiencePct = rec.matchBasisExperience ? 1 : 0;
                const isCombined =
                  rec.matchCategory === "combined" ||
                  ((skillsPct > 0 ? 1 : 0) + (educationPct > 0 ? 1 : 0) + experiencePct >= 2);
                const max = Math.max(skillsPct, educationPct, experiencePct);
                const strong = isCombined && max > 0
                  ? skillsPct === max ? "skills" : educationPct === max ? "education" : "experience"
                  : null;
                const strongStyle = { fontWeight: 800 as const, color: "#1d4ed8" };

                return (
                  <div className="hb-recommendation-match-basis">
                    <div className="hb-match-basis-items">
                      {rec.matchBasisSkillsMatched !== undefined && (
                        <div className="hb-match-basis-item" style={strong === "skills" ? strongStyle : undefined}
                             aria-label={strong === "skills" ? "Strongest dimension: Skills" : undefined}>
                          <span className="hb-match-basis-label">💻 Skills</span>
                          <span className="hb-match-basis-value">
                            {rec.matchBasisSkillsMatched}/{rec.matchBasisSkillsTotal}
                            {strong === "skills" && " ★"}
                          </span>
                        </div>
                      )}
                      {rec.matchBasisEducationMatched !== undefined && (
                        <div className="hb-match-basis-item" style={strong === "education" ? strongStyle : undefined}
                             aria-label={strong === "education" ? "Strongest dimension: Education" : undefined}>
                          <span className="hb-match-basis-label">🎓 Education</span>
                          <span className="hb-match-basis-value">
                            {rec.matchBasisEducationMatched}/{rec.matchBasisEducationTotal}
                            {strong === "education" && " ★"}
                          </span>
                        </div>
                      )}
                      {rec.matchBasisExperience !== undefined && (
                        <div className="hb-match-basis-item" style={strong === "experience" ? strongStyle : undefined}
                             aria-label={strong === "experience" ? "Strongest dimension: Experience" : undefined}>
                          <span className="hb-match-basis-label">💼 Experience</span>
                          <span className="hb-match-basis-value">
                            {rec.matchBasisExperience ? "✓ Match" : "⚠ Level"}
                            {strong === "experience" && " ★"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Matched Skills */}
              {rec.matchedSkills.length > 0 && (
                <div className="hb-recommendation-skills">
                  <div className="hb-recommendation-skills-label">
                    ✓ Your Matching Skills ({rec.matchedSkills.length})
                  </div>
                  <div className="hb-recommendation-skills-list">
                    {rec.matchedSkills.map((skill, idx) => (
                      <span key={idx} className="hb-badge hb-badge-primary">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Reason */}
              {rec.reason && (
                <div className="hb-recommendation-reason">
                  <p className="hb-recommendation-reason-text">💡 {rec.reason}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="hb-recommendation-actions">
                {rec.url && (
                  <a
                    href={rec.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hb-btn hb-btn-primary"
                  >
                    View Opportunity
                  </a>
                )}
                <button
                  onClick={() => ignoreRecommendation(rec.jobId)}
                  className="hb-btn hb-btn-outline"
                >
                  Not Interested
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default JobRecommendations;
